import {t} from '@lingui/macro';
import {isEqual} from 'lodash';
import {DateTime, Interval} from 'luxon';

import {globalWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {
  getCalendarEventReminders,
  makeStartEndDateTimeForCalendarEventVersion,
} from '../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {
  buildCalendarNotificationLink,
  buildCalendarReminderTimeText,
} from '../../helpers/calendarReminderHelpers';
import {AppState} from '../../store/appState';
import {AppStore} from '../../store/appStore';
import {makeFindGetCalendarEventParametersList} from '../../store/data/calendarEventVersions/calendarEventVersionsSelectors';
import {getSelectedCalendarVersions} from '../../store/data/calendarVersions/calendarVersionsSelectors';
import {getCalendarWeekStartDay, getTimeFormat} from '../../store/data/preferences/preferencesSelectors';
import {
  navigateCalendarsAction,
  navigateExistingCalendarsAction,
} from '../../store/panels/calendars/calendarsPanelActions';
import {CalendarsParameters, CalendarsViewTypesEnum} from '../../store/panels/calendars/calendarsParameters';
import {findParametersRange} from '../../store/panels/calendars/calendarsParametersHelpers';
import {CalendarsPanelId, makeCalendarsPanelId, PanelTypesEnum} from '../../store/panels/panelStateBase';
import {getLocalize} from '../../store/stateless/internationalizationSelectors';
import {showNotificationAction} from '../../store/stateless/notificationsActions';

/*
 * Setup.
 */

// Start notification service 60s after app starts to avoid slowing down initialization.
const initialDelay = 60 * 1000;

// Check if we need to send any reminders every minute.
const checkRemindersInterval = 60 * 1000;

let checkRemindersTimeout: number | undefined;
let remindersPanelId: CalendarsPanelId | undefined;

export function setupReminderNotifications(store: AppStore) {
  globalWindow.setInstrumentedTimeout(() => {
    initializeReminders(store);
  }, initialDelay);
}

function initializeReminders(store: AppStore) {
  // If we've already created our reminder notification panel, nothing to do.
  if (remindersPanelId) {
    return;
  }

  remindersPanelId = makeCalendarsPanelId();
  store.dispatch(navigateCalendarsAction(remindersPanelId, buildParameters(store.getState())));

  startReminders(store);

  store.subscribe(() => {
    startReminders(store);
  });
}

function startReminders(store: AppStore) {
  if (!checkRemindersTimeout) {
    checkRemindersTimeout = globalWindow.setInstrumentedTimeout(() => {
      checkReminders(store);
    }, checkRemindersInterval);
  }
}

/*
 * Monitor.
 */

async function checkReminders(store: AppStore) {
  try {
    await maybeFetchEvents(store);
  } catch (e) {
    // If we're unable to fetch events b/c of network or other issues, just send reminders using our local state.
  }

  const state = store.getState();
  const localize = getLocalize(state);
  const {calendarVersions, calendarEventVersions} = state.data;

  // If we don't yet have a view because we haven't successfully fetched events yet, try again in the next cycle.
  const remindersPanel = getRemindersCalendarPanel(state);
  const getEventParametersList = makeFindGetCalendarEventParametersList(
    () => remindersPanel.parameters,
    () => remindersPanel.calendarEventVersionUids,
  );
  const eventParametersList = getEventParametersList(state, {});

  const now = DateTime.local().startOf('minute');
  const interval = Interval.fromDateTimes(now, now.plus({minutes: 1}));

  eventParametersList().forEach((p) => {
    const calendarVersion = calendarVersions[p.calendarVersionId];
    const calendarEventVersion = calendarEventVersions[p.calendarEventVersionUid];
    if (!calendarVersion || !calendarEventVersion) {
      return;
    }

    const {startDateTime, endDateTime} = makeStartEndDateTimeForCalendarEventVersion(
      calendarEventVersion,
      p.instanceStartDate ? DateTime.fromMillis(p.instanceStartDate) : undefined,
    );

    // If this event is in the past we don't have any reminders to send.
    if (startDateTime < now) {
      return;
    }

    const reminders = getCalendarEventReminders(calendarVersion, calendarEventVersion);
    reminders.forEach((r) => {
      if (interval.contains(startDateTime.startOf('minute').minus({minutes: r.minutesBefore}))) {
        const notificationOptions = {
          title:
            calendarEventVersion.summary ||
            localize(t('calendar-reminder-notifications-untitled')`Untitled meeting`),
          body: buildCalendarReminderTimeText(
            startDateTime,
            endDateTime,
            calendarEventVersion.isAllDay,
            getTimeFormat(state),
          ),
        };

        store.dispatch(
          showNotificationAction(
            notificationOptions,
            buildCalendarNotificationLink(p, startDateTime.toMillis()),
          ),
        );
      }
    });
  });

  checkRemindersTimeout = globalWindow.setInstrumentedTimeout(() => {
    checkReminders(store);
  }, checkRemindersInterval);
}

async function maybeFetchEvents(store: AppStore) {
  if (!remindersPanelId) {
    throw new Error('Unexpected to fetch events without calendar reminders panel.');
  }

  const state = store.getState();
  if (!shouldRefetchEvents(state)) {
    return;
  }

  await store.dispatch(navigateExistingCalendarsAction(remindersPanelId, buildParameters(state)));
}

function getRemindersCalendarPanel(state: AppState) {
  if (!remindersPanelId) {
    throw new Error('Unexpected to check reminders without a reminders panel ID.');
  }

  const panel = state.panels[remindersPanelId];
  if (!panel || panel.type !== PanelTypesEnum.CALENDARS) {
    throw new Error('Unexpected to check reminders without a reminders panel.');
  }

  return panel;
}

function buildParameters(state: AppState): CalendarsParameters {
  return {
    viewType: CalendarsViewTypesEnum.REMINDERS,
    focusedDate: Date.now(),
    weekStartDay: getCalendarWeekStartDay(state),
    selectedCalendarVersionIds: getSelectedCalendarVersions(state).map((v) => v.id),
    viewingNowCalendarVersionIds: [],
  };
}

// Our parameters are "outdated" if one of the following is true:
// 1. We don't have a view and have therefore never fetched.
// 2. The start of the view range is over 24 hours ago.
// 2. The calendarVersionIds do not match our selected calendar version IDs.
function shouldRefetchEvents(state: AppState) {
  const {parameters} = getRemindersCalendarPanel(state);

  // Check if any of the Calendar Version IDs have changed.
  const newSelectedCalendarVersionIds = getSelectedCalendarVersions(state).map((v) => v.id);
  const {selectedCalendarVersionIds} = parameters;

  if (!isEqual(newSelectedCalendarVersionIds, selectedCalendarVersionIds)) {
    return true;
  }

  // If not, check that we're still on the first day of the range.
  const {startsAt} = findParametersRange(parameters);
  return DateTime.fromMillis(startsAt).plus({days: 1}) < DateTime.local();
}
